import type { ComponentType } from 'react';
import type { View } from '@/app/navigation/navigation-state';
import { homeViews } from '@/app/views/home';
import { obrasViews } from '@/app/views/obras';
import { comercialViews } from '@/app/views/comercial';
import { financeiroViews } from '@/app/views/financeiro';
import { suprimentosViews } from '@/app/views/suprimentos';
import { configViews } from '@/app/views/config';
import { adminViews } from '@/app/views/admin';
import { atividadeViews } from '@/app/views/atividade';
import { projetosViews } from '@/app/views/projetos';
import { pdfViews } from '@/app/views/pdf';
import { toolsViews } from '@/app/views/tools';
import { tarefasViews } from '@/app/views/tarefas';

/**
 * Guard spec forwarded to <ProtectedView>. Exactly one of `screen` / `permission`
 * should be set (or neither, for views that render without a guard).
 */
export type GuardSpec =
  | { screen: string; permission?: never }
  | { permission: string | string[]; screen?: never }
  | { screen?: undefined; permission?: undefined };

export type ViewLayout = 'default' | 'full-bleed';

export interface ViewEntry {
  /**
   * Renderable adapter component. Adapters consume useNavigation() internally
   * and render the actual view component with the correct props for that view.
   *
   * This type intentionally accepts both regular components and LazyExoticComponent —
   * code-splitting happens inside the adapter via `lazy(() => import(...))`,
   * so the adapter itself need not be lazy. Router's <Suspense> still triggers
   * on the inner lazy load.
   */
  component: ComponentType<unknown>;
  /**
   * Permission/screen guard forwarded to <ProtectedView>. When both fields are
   * absent the view renders without a ProtectedView wrapper.
   */
  guard: GuardSpec;
  /**
   * Page chrome strategy. `'default'` (or omitted) wraps the view in
   * <PageShell> so it inherits the standard 100%×100% surface with 10px
   * gutters. `'full-bleed'` skips the wrapper — reserve for views that own
   * their own layout (immersive viewers, full-screen tooling).
   */
  layout?: ViewLayout;
}

export type ViewRegistry = Record<View, ViewEntry>;

// The final `as ViewRegistry` cast is the single place where we assert
// exhaustiveness at the type level. TS cannot verify that every literal in
// the View union appears as a key here (only the reverse check is free), so
// the DEV-time assertion below catches drift at app boot.
export const viewRegistry: ViewRegistry = {
  ...homeViews,
  ...obrasViews,
  ...comercialViews,
  ...financeiroViews,
  ...suprimentosViews,
  ...configViews,
  ...adminViews,
  ...atividadeViews,
  ...projetosViews,
  ...pdfViews,
  ...toolsViews,
  ...tarefasViews,
} as ViewRegistry;

// DEV-time completeness assertion. Keep the `requiredViews` list in sync with
// the View union in navigation-state.ts. Vite dead-code-eliminates this in
// production builds via `import.meta.env.DEV`.
if (import.meta.env.DEV) {
  const seen = new Set(Object.keys(viewRegistry));
  const requiredViews: View[] = [
    'home',
    'obras', 'obras-dashboard', 'obras-jornadas', 'obras-areas',
    'obras-ambientes', 'obras-pontos', 'obras-produtos', 'obras-cabos',
    'obras-tubos', 'obras-pessoas', 'obras-servicos', 'obras-vistorias',
    'obras-andamentos', 'obras-cronograma',
    'os-ativas', 'os-agenda',
    'oportunidades-ativas-v2', 'oportunidades-encerradas-v2',
    'proposta-details', 'proposta-parametro', 'proposta-grupo-imagem-manager',
    'oportunidade-status',
    'bancos', 'contas-bancarias', 'centros-custo', 'categorias-contabeis',
    'formas-pagamento', 'contas-receber', 'contas-pagar', 'movimentos', 'caixa',
    'comissao', 'comissao-parametro', 'comissao-bonus', 'vendas',
    'proposta-resultado',
    'banco-details', 'conta-details', 'centro-custo-details',
    'suprimentos-requisicoes-ativas', 'suprimentos-requisicoes-encerradas',
    'suprimentos-movimentacao', 'suprimentos-balanco',
    'suprimentos-cat', 'suprimentos-triagem', 'suprimentos-ordem-compra',
    'suprimentos-ordem-compra-status', 'suprimentos-marca',
    'suprimentos-produto-categoria', 'suprimentos-produto-subcategoria',
    'suprimentos-produto-tipo', 'suprimentos-unidade-medida',
    'suprimentos-margem-tipo', 'suprimentos-classificacao',
    'pessoas', 'empreendimentos', 'departamentos', 'estados', 'cidades', 'fornecedores',
    'produtos', 'tipos-ambiente', 'tipos-ponto', 'grupos-tipo-ponto',
    'tipos-servico', 'tipos-vistoria', 'profissional-tipo', 'atraso-motivo',
    'file-categorias', 'transition-rules', 'obra-etapa', 'obra-produto-status',
    'modulos', 'departamento-details', 'estado-details', 'cidade-details',
    'tipo-ambiente-details', 'tipo-ponto-details', 'grupo-tipo-ponto-details',
    'api-keys', 'api-keys-admin',
    'api-docs',
    'webhooks',
    'equipe', 'perfis', 'auditoria-logs',
    'atividade-consultor', 'atividade-config-areas', 'atividade-config-tipos',
    'projetos-ativos', 'projetos-encerrados', 'projeto-status',
    'pdf-template-manager', 'pdf-layout-builder',
    'painel-cargas', 'orcamento-escopo', 'config',
    'cable-routing', 'google-chat-manager',
    'sync-os-fotos', 'auto-services', 'produtos-a-comprar', 'notificacoes',
    'tarefas',
  ];
  const missing = requiredViews.filter((v) => !seen.has(v));
  if (missing.length > 0) {
    throw new Error(
      `[view-registry] Missing entries for views: ${missing.join(', ')}. ` +
        'Add them to the corresponding src/app/views/*.tsx module.',
    );
  }
}
